/**
 * ScrubPreview — the thumbnail and time chip above the scrubber, for the
 * pointer or a hovered note marker. The frame comes from a hidden second
 * <video> (seeking the player would move its playhead), throttled and painted
 * to a canvas; a note with a captured frame shows that frame instead.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { useMediaQuery } from '../../../../../lib/browser/useMediaQuery';
import { NO_TIMEOUT, useTimeout } from '../../../../../lib/browser/useTimeout';
import { cn } from '../../../../../lib/utils';
import { formatTimecode } from '../../../../../lib/media/timecode';
import { FrameSnapshot, type PreviewSnapshot } from './FrameSnapshot';
import { PREVIEW_BOX_CLASS, PREVIEW_NOTE_CLASS, PREVIEW_NOTE_STYLE, previewBoxStyle, previewSize, type PreviewNote } from './previewFrame';
import { READY_STATE_HAVE_CURRENT_DATA } from './useMediaController';

const MS_PER_SECOND = 1000;
/** Seeking a second decoder faster than this stutters the main player. */
export const PREVIEW_SEEK_INTERVAL_MS = 150;
/** Outside the throttle window a new hover target is seeked on the next tick. */
const SEEK_NOW_MS = 0;
/** The canvas backing store is drawn at 2× the CSS size for retina. */
const PREVIEW_BACKING_SCALE = 2;
/** Touch devices have no hover, so the hidden decoder would never pay off. */
const HOVER_NONE_QUERY = '(hover: none)';

/** What the preview floats above: the rail under the pointer, or a note marker in the row above it. */
export type PreviewPlacement = 'rail' | 'marker';

/** Offsets from the rail's bottom edge; a marker's box clears the 11px marker row as well. */
const PLACEMENT_CLASS: Record<PreviewPlacement, string> = {
  rail: 'bottom-[26px]',
  marker: 'bottom-[33px]',
};

export interface ScrubPreviewProps {
  visible: boolean;
  /** The time under the pointer (or the hovered marker), not the playhead. */
  timeMs: number;
  /** Offset from the rail's left edge, already clamped. */
  leftPx: number;
  src: string;
  crossOrigin?: 'anonymous';
  /** Metadata has loaded — before that the hidden copy has nothing to seek to. */
  ready: boolean;
  portrait: boolean;
  placement?: PreviewPlacement;
  /** A note's captured frame, shown in place of the decoded one. */
  snapshot?: PreviewSnapshot;
  /** A hovered note's author and text, under the time. */
  note?: PreviewNote;
}

/** A React node, never innerHTML — note bodies are user text. */
function PreviewNoteBox({ note }: { note: PreviewNote }) {
  return (
    <span className={PREVIEW_NOTE_CLASS} style={PREVIEW_NOTE_STYLE} data-testid="marker-preview-note">
      <b className="block font-semibold text-white">{note.author}</b>
      {note.snippet ? <span className="block text-white/80">{note.snippet}</span> : null}
    </span>
  );
}

/** Re-keys the snapshot per frame, so a failed one never blocks the next note's. */
function snapshotKey({ fileId, localUrl }: PreviewSnapshot): string {
  return localUrl ?? fileId ?? '';
}

/**
 * Mount it keyed by `src`: a refreshed presigned URL is the same video through
 * a new decoder, so the thumbnail gets another chance and the previous cut's
 * frame is never shown.
 */
export function ScrubPreview({ visible, timeMs, leftPx, src, crossOrigin, ready, portrait, placement = 'rail', snapshot, note }: ScrubPreviewProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // A tainted frame throws on draw — fall back to the time chip alone.
  const [drawable, setDrawable] = useState(true);
  // An empty canvas is worse than no canvas, so it only appears once a real
  // frame has landed in it.
  const [drawn, setDrawn] = useState(false);
  // Where the hidden decoder was last sent; null before its first seek.
  const [seekedToMs, setSeekedToMs] = useState<number | null>(null);
  // Open for one interval after every seek: the throttle's closed window.
  const [coolingDown, setCoolingDown] = useState(false);
  const coarsePointer = useMediaQuery(HOVER_NONE_QUERY);
  // The hidden decoder starts loading only once the track is first hovered or focused — most reviews never scrub.
  const [engaged, setEngaged] = useState(false);
  if (visible && !engaged) setEngaged(true);

  const decoderEnabled = engaged && ready && drawable && !coarsePointer;
  const size = previewSize(portrait);

  const draw = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    try {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      setDrawn(true);
    } catch {
      setDrawable(false);
    }
  }, []);

  // A canvas mounted for the time the decoder already sits on gets no new
  // `seeked` (hovering the same marker twice), so it paints the held frame now.
  const attachCanvas = useCallback(
    (canvas: HTMLCanvasElement | null) => {
      canvasRef.current = canvas;
      const video = videoRef.current;
      if (!canvas || !video) return;
      if (video.readyState < READY_STATE_HAVE_CURRENT_DATA) return;
      draw();
    },
    [draw],
  );

  useEffect(() => {
    const video = videoRef.current;
    if (!decoderEnabled || !video) return undefined;
    // `draw` reads both refs lazily, so this survives the visible box (and its
    // canvas) mounting and unmounting as the pointer comes and goes.
    // A seek asked for before its metadata arrived may be dropped: ask again once it has.
    const reseek = () => setSeekedToMs(null);
    video.addEventListener('seeked', draw);
    video.addEventListener('loadedmetadata', reseek);
    return () => {
      video.removeEventListener('seeked', draw);
      video.removeEventListener('loadedmetadata', reseek);
    };
  }, [decoderEnabled, draw]);

  const seekToHover = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = timeMs / MS_PER_SECOND;
    setSeekedToMs(timeMs);
    setCoolingDown(true);
  }, [timeMs]);

  const endCooldown = useCallback(() => setCoolingDown(false), []);

  // Leading edge: a new target outside the window goes out at once. Trailing
  // edge: one that arrived inside it goes out as the window closes — the
  // pointer usually stops somewhere, and that is the frame the reviewer wants.
  // It seeks under a snapshot too, so a frame that fails to load falls back at once.
  const seekDue = decoderEnabled && visible && !coolingDown && timeMs !== seekedToMs;
  useTimeout(seekToHover, seekDue ? SEEK_NOW_MS : NO_TIMEOUT);
  useTimeout(endCooldown, coolingDown ? PREVIEW_SEEK_INTERVAL_MS : NO_TIMEOUT);

  function decodedFrame(): ReactNode {
    if (!decoderEnabled) return null;
    return (
      <canvas
        ref={attachCanvas}
        width={size.width * PREVIEW_BACKING_SCALE}
        height={size.height * PREVIEW_BACKING_SCALE}
        // Mounted but not shown until a frame has actually landed: a canvas 2D
        // context draws fine while `display:none`, and a blank black box under
        // the pointer reads as a broken thumbnail.
        className={cn(PREVIEW_BOX_CLASS, !drawn && 'hidden')}
        style={previewBoxStyle(size)}
      />
    );
  }

  function thumbnail(): ReactNode {
    if (!snapshot) return decodedFrame();
    return <FrameSnapshot key={snapshotKey(snapshot)} snapshot={snapshot} size={size} fallback={decodedFrame()} />;
  }

  return (
    <>
      {decoderEnabled && (
        // Kept in the layout at 1×1 rather than `display:none` — some browsers
        // refuse to seek (and therefore to decode) a fully hidden video.
        <video
          ref={videoRef}
          src={src}
          crossOrigin={crossOrigin}
          muted
          preload="metadata"
          playsInline
          aria-hidden="true"
          tabIndex={-1}
          className="pointer-events-none absolute left-0 top-0 size-px opacity-0"
        />
      )}
      {visible && (
        <div
          data-testid="scrub-preview"
          data-placement={placement}
          className={cn('pointer-events-none absolute z-[8] flex -translate-x-1/2 flex-col items-center gap-[5px]', PLACEMENT_CLASS[placement])}
          style={{ left: `${leftPx}px` }}
        >
          {thumbnail()}
          <span className="rounded-full bg-(--background-player)/90 px-2 py-[2px] text-[11px] font-semibold tabular-nums text-white">
            {formatTimecode(timeMs)}
          </span>
          {note ? <PreviewNoteBox note={note} /> : null}
        </div>
      )}
    </>
  );
}
